import { GridComponent, IIndex } from '@syncfusion/ej2-react-grids';
import { TFunction } from 'i18next';

import { ShowableTaxReturnItemRecord } from '../components/add-return/AddReturnPanel';
import { ITaxReturnTab } from '../contexts/userSettingsContext';
import { IFilterItem } from '../data-types/filterItem';
import { IExecutionStatusStorage } from '../data-types/IExecutionStatusStorage';
import { IRowInfo } from '../data-types/IRowInfo';
import {
    CalculationExecutionStatus,
    CalculationExecutionStatusCode,
    CustomGroup,
    CustomRange,
    IWhitePaperColumn,
    IWhitePaperComparedValue,
    IWhitePaperReport,
    IWhitePaperRow,
    Report,
    ReportListRequest,
    ReviewFormField,
    TaxReturnTOCItem,
    TaxReturnTOCKey,
    ViewFormResponseFormFields,
    WhitePaperReportExportRequest,
    WhitePaperReportVersion,
} from '../model';
import { getColumnNames, SUBTOTAL_COLUMN_NAME } from './ColumnUtils';
import { getDateStringAsLocaleString } from './DateUtils';
import { CommandType } from './EventGrid.constants';
import { formatNumberForDisplay, parseNumericValue } from './NumberUtils';
import { isUndefinedOrEmpty } from './StringUtils';
import {
    currentValueFieldNameSuffix,
    dynamicColumnsFieldNameBeginning,
    squareBracketsClosing,
    squareBracketsOpening,
    varianceValueFieldNameSuffix,
} from './Table.constants';
import { getCellValueForColumnInWhitePaperRow } from './TableUtils';

const MULTIPLE_REPORTS_EXPORT_LIMIT = 20;
export const CLICKABLE_COLUMN_START_INDEX = 2;

export const updateSubtotalByEntitiesValue = (rows: IWhitePaperRow[], columns: string[], treeGridRef: any) => {
    if (!columns.some((columnName) => columnName.includes(SUBTOTAL_COLUMN_NAME))) {
        return;
    }

    rows.forEach((row) => {
        if (row.values) {
            const value: any = row.values;
            let subtotalValue = 0;
            let subtotalColumnName = SUBTOTAL_COLUMN_NAME;
            columns.forEach((column) => {
                if (shouldAddColumnValueToSubtotal(value, column)) {
                    const numericValue = parseNumericValue(value[column]?.current);
                    subtotalValue += numericValue;
                } else if (column.includes(SUBTOTAL_COLUMN_NAME)) {
                    subtotalColumnName = column;
                }
            });

            value[subtotalColumnName].current = formatNumberForDisplay(subtotalValue);
        }
    });
    treeGridRef?.current?.clearSelection();
    treeGridRef?.current?.refresh();
};

export const shouldAddColumnValueToSubtotal = (value: any, columnName: string): boolean => {
    return value && columnName && !columnName.includes(SUBTOTAL_COLUMN_NAME) && value[columnName]?.current;
};

export const getVisibleEntityColumnNames = (filteredItems: IFilterItem[], columns: IWhitePaperColumn[]): string[] => {
    const filteredEntityNames = getColumnNames(filteredItems);
    return columns
        .filter((column) => column.entityName && filteredEntityNames.includes(column.entityName))
        .map((column) => column.fieldName ?? '');
};

export const getDynamicFieldName = (fieldName: string | undefined | null, sufixAttributeName: string): string => {
    if (!fieldName) {
        return '';
    }
    if (!sufixAttributeName && fieldName) {
        return fieldName;
    }
    return dynamicColumnsFieldNameBeginning + '.' + fieldName + sufixAttributeName;
};

export const getDynamicFieldNameParts = (fieldName: string) => {
    let cleanedName = fieldName.replace(dynamicColumnsFieldNameBeginning, '');

    if (cleanedName.startsWith('.')) {
        cleanedName = cleanedName.replace('.', '');
    }

    const parts = cleanedName.replace(squareBracketsOpening, '').replace(squareBracketsClosing, '').split('.');

    const isComplexValue = parts.length === 2;
    const entityCode = parts[0];
    const valueType = isComplexValue ? parts[1] : '';

    return { entityCode, valueType, isComplexValue };
};

export const isNegativeValue = (value: string | undefined): boolean => value?.startsWith('(') ?? false;

export const getCellValueByFieldName = (item: IWhitePaperRow, fieldName: string): string => {
    const { entityCode, valueType, isComplexValue } = getDynamicFieldNameParts(fieldName);
    if (isComplexValue) {
        return getNumericValue(item, entityCode, valueType);
    } else {
        const propertyKey = fieldName as keyof IWhitePaperRow;
        return item[propertyKey]?.toString() ?? '';
    }
};

const getNumericValue = (item: IWhitePaperRow, entityCode: string, valueType: string): string => {
    if (item.values) {
        const valuesForEntity = item.values[entityCode];
        const fieldNameAsKey = valueType as keyof IWhitePaperComparedValue;

        return valuesForEntity[fieldNameAsKey] as string;
    }

    return '';
};

export const getSelectedReportRequestsToExport = (
    selectedRecords: Report[] | undefined,
    translateFunction: TFunction<'translation', undefined, 'translation'>,
    compareVersionId?: string
): WhitePaperReportExportRequest[] => {
    if (selectedRecords && selectedRecords.length > 0 && selectedRecords.length <= MULTIPLE_REPORTS_EXPORT_LIMIT) {
        if (selectedRecords.every((report) => report.executed)) {
            return selectedRecords.map((report) => {
                return {
                    id: report.id ?? 0,
                    period: report.period ?? '',
                    entityCode: report.entityCode ?? '',
                    isCompare: compareVersionId !== undefined,
                    versionId: compareVersionId,
                };
            });
        } else {
            throw translateFunction('notExecutedReportSelectedForExportError');
        }
    } else if (selectedRecords && selectedRecords.length > MULTIPLE_REPORTS_EXPORT_LIMIT) {
        throw translateFunction('multipleReportsSelectedForExportError');
    } else {
        throw translateFunction('noReportsSelectedForExportError');
    }
};

export const getExportFileNameBasedOnSelectedReports = (
    selectedRecords: WhitePaperReportExportRequest[] | undefined,
    fileExtension: string,
    translateFunction: TFunction<'translation', undefined, 'translation'>,
    isTaxReturnList?: boolean
) => {
    if (!isTaxReturnList && selectedRecords) {
        if (selectedRecords.length === 1) {
            const compareTag = selectedRecords[0].isCompare ? `${translateFunction('titleCompare')} ` : '';
            return `${compareTag}${selectedRecords[0].name} ${translateFunction('report')?.toUpperCase()}${fileExtension}`;
        } else {
            const currentDate = new Date().toLocaleDateString(undefined, { dateStyle: 'short' });
            return `${translateFunction('reportExport')} - ${currentDate}${fileExtension}`;
        }
    }

    return `${translateFunction('taxReturns')}${fileExtension}`;
};

export const getOnlyExceedingRows = (rows: IWhitePaperRow[]): IWhitePaperRow[] => {
    return rows.filter((row) => {
        const values = row.values ?? [];
        for (const [, value] of Object.entries<IWhitePaperComparedValue>(values)) {
            if (value.exceedsVariance) return true;
        }

        return false;
    });
};

export const getNonZeroRows = (rows: IWhitePaperRow[]): IWhitePaperRow[] => {
    return rows.filter((row) => {
        const values = row.values ?? [];
        for (const [, value] of Object.entries<IWhitePaperComparedValue>(values)) {
            if (!isUndefinedOrEmpty(value.variance) && !isZeroValue(value.variance ?? '')) return true;
        }

        return false;
    });
};

const isZeroValue = (value: string) => {
    return parseNumericValue(value.replace('%', '')) === 0;
};

export const updateEmptyRowTextInGrid = (grid: GridComponent, emptyRowText: string): void => {
    const emptyRowElement = grid.getContent().querySelector('.e-emptyrow')?.firstElementChild;

    if (emptyRowElement) {
        emptyRowElement.innerHTML = emptyRowText;
    }
};

export const parseRowValuesToNumber = (row: IWhitePaperRow) => {
    for (const entity in row.values) {
        const currentEntityValue = row.values[entity];
        const valueAsNumber = parseNumericValue(currentEntityValue.current ?? '');

        if (currentEntityValue.current) {
            currentEntityValue.current = valueAsNumber.toString();
        }
    }
};

export const formatEditableRowValues = (row: IWhitePaperRow) => {
    for (const entity in row.values) {
        const currentEntityValue = row.values[entity];
        const valueAsNumber = parseNumericValue(currentEntityValue.current ?? '');

        if (currentEntityValue.current) {
            const formattedNumber = formatNumberForDisplay(valueAsNumber);
            currentEntityValue.current = formattedNumber;
        }
    }
};

export const isInputRow = (row?: IWhitePaperRow): boolean => {
    return row?.definition?.toLowerCase() === 'input';
};

export const markModifiedValues: (row: IWhitePaperRow, previousData: IWhitePaperRow) => boolean = (
    row: IWhitePaperRow,
    previousData: IWhitePaperRow
) => {
    let isRowModified = false;
    if (row && row.values && previousData && previousData.values) {
        for (const entity in row.values) {
            const currentEntityValue: IWhitePaperComparedValue = row.values[entity];
            const previousEntityvalue: IWhitePaperComparedValue = previousData.values[entity];
            const currentValueAsNumber: number = parseNumericValue(currentEntityValue.current ?? '');
            const previousValueAsNumber: number = parseNumericValue(previousEntityvalue.current ?? '');

            currentEntityValue.isOverridden = currentValueAsNumber !== previousValueAsNumber;
            isRowModified = isRowModified || currentEntityValue.isOverridden;
        }
    }
    return isRowModified;
};

export const shouldReportBeOnTopOfRecentlyOpened = (report: Report | undefined, recentlyOpened: ITaxReturnTab[] | undefined): boolean => {
    const reportId = Number(report?.id);
    const result: boolean =
        reportId > 0 && recentlyOpened !== undefined && recentlyOpened.length > 0 && recentlyOpened[0].returnKey !== reportId;
    return result;
};

export const getReportPropertyValueByFieldName = (report: Report, fieldName: string): string => {
    const reportPropertyKey = fieldName as keyof Report;

    return report[reportPropertyKey]?.toString() ?? '';
};

export const getColumnFieldNameByIndex = (index: number, currentReport?: IWhitePaperReport): string => {
    if (!currentReport?.columns) {
        return '';
    }

    if (index < 0 || currentReport.columns.length <= index) {
        return '';
    }

    return currentReport.columns[index].fieldName ?? '';
};

export const scrollToRowAndSetFocusCell = (
    setIsVarianceNavigating: (isNavigating: boolean) => void,
    setCurrentVarianceIndex: (step: number) => void,
    scrollFunction: (gridRef: GridComponent, index: IIndex) => void,
    cellIndex: IIndex,
    step: number,
    grid?: GridComponent
) => {
    if (grid) {
        setIsVarianceNavigating(true);
        scrollFunction(grid, cellIndex);
        setCurrentVarianceIndex(step);
    }
};

const isOriginalColumnIdGreaterThanZero = (column: IWhitePaperColumn) => (!column?.originalColumnId ? false : column?.originalColumnId > 0);

export const TOTAL_COLUMN_ID = 99991;
export const ELIMINATION_COLUMN_ID = 99992;
export const SUBTOTAL_COLUMN_ID = 99993;

export const isDrillDownContextMenuAllowed = (
    cellIndex: IRowInfo<IWhitePaperRow>,
    isMultiColumnReport: Boolean,
    currentReport: IWhitePaperReport | undefined,
    isDrillDownAllowed: (cellIndex?: IIndex) => boolean
) => {
    if ((cellIndex?.rowData as any)?.['definition'] && isDrillDownAllowed?.(cellIndex) && currentReport?.columns?.length) {
        const currentColumnName = cellIndex.column?.headerText?.replace(' Current', '')?.toLocaleLowerCase();
        const column: IWhitePaperColumn | undefined = currentReport.columns.find(
            (col) => col.entityName?.toLocaleLowerCase() === currentColumnName
        );
        let isTotalOrSubTotalColumn = false;
        if (column?.originalColumnId == TOTAL_COLUMN_ID || column?.originalColumnId == SUBTOTAL_COLUMN_ID) {
            isTotalOrSubTotalColumn = true;
        }

        return column && (!isMultiColumnReport || (isOriginalColumnIdGreaterThanZero(column) && !isTotalOrSubTotalColumn));
    }
    return false;
};

export const getReportVersionLabelText = (version: WhitePaperReportVersion): string => {
    return `${getDateStringAsLocaleString(version.dateTime, true)} - ${version.executedBy}`;
};

export function isEditAllowedForCell(isMultiColumns: boolean, cellIndex: IIndex): boolean {
    let canEdit: boolean = false;
    if (isMultiColumns) {
        canEdit =
            (cellIndex.cellIndex ?? 0) - 1 === CLICKABLE_COLUMN_START_INDEX ||
            (cellIndex.cellIndex ?? 0) - 2 > CLICKABLE_COLUMN_START_INDEX;
    } else {
        canEdit = isMenuEnabledForTableColumn(cellIndex.cellIndex ?? 0);
    }
    return canEdit;
}

export const isMenuEnabledForTableColumn = (cellIndex: number): boolean => {
    return cellIndex >= CLICKABLE_COLUMN_START_INDEX;
};

export function getLegacyReturnRequest(
    columnFilters: Record<string, IFilterItem[]>,
    folderKey: number | undefined,
    entityFilterKey: string,
    jurisdictionFilterKey: string,
    periodFilterKey: string,
    caseFilterKey: string
): ReportListRequest {
    const filter = {
        selectedEntities: columnFilters[entityFilterKey]?.map((x) => x.key) ?? [],
        selectedJurisdictions: columnFilters[jurisdictionFilterKey]?.map((x) => x.key) ?? [],
        selectedPeriods: columnFilters[periodFilterKey]?.map((x) => x.key) ?? [],
        selectedCases: columnFilters[caseFilterKey]?.map((x) => x.key) ?? [],
    };

    if (folderKey) {
        return {
            ...filter,
            folderKey: folderKey,
        };
    }

    return filter;
}

export function getReturnRequest(folderKey: number | undefined): ReportListRequest {
    if (folderKey) {
        return { folderKey: folderKey };
    }

    return {};
}

export const shouldFetch = (
    currentReportInfo: Report | undefined,
    currentReturnStatus: CalculationExecutionStatus,
    hasContextChanged: boolean
) => (currentReportInfo?.executed || currentReturnStatus.statusCode === CalculationExecutionStatusCode.Completed) && !hasContextChanged;

export const shouldExecuteRerun = (currentReportInfo: Report, isReportExecuting: boolean, isLoadingRecalculation: boolean): boolean => {
    return (
        (!currentReportInfo.executed || Boolean(currentReportInfo.hasUpdatedCollateral)) && (!isReportExecuting || !isLoadingRecalculation)
    );
};

export const getRecentlyOpenedTabsWithStatus = (
    calculationExecutionStatus: IExecutionStatusStorage[],
    recentlyOpenedTabs: ITaxReturnTab[] | undefined
): ITaxReturnTab[] => {
    if (recentlyOpenedTabs) {
        return recentlyOpenedTabs.map((item) => {
            let statusCode: CalculationExecutionStatusCode = CalculationExecutionStatusCode.Unknown;
            let commandType: CommandType = CommandType.Execute;
            const index = calculationExecutionStatus.findIndex((i) => i.taxReturnItemKey === item.returnKey);
            if (index >= 0) {
                statusCode = calculationExecutionStatus[index].statusCode ?? CalculationExecutionStatusCode.Unknown;
                commandType = calculationExecutionStatus[index].commandType ?? CommandType.Execute;
            }

            return {
                ...item,
                statusCode,
                commandType,
            };
        });
    }

    return [];
};

// we might consider making this take an element (not an event) and then it might be callable from more places if needed
// there are a variety of problems with this approach including that the cell isn't truly focused, it doesn't lose
// the border if you click outside the table, etc
// there are ways to set focus, but it wouldn't help the situation where the user is opening the panel, as only one element
// at a time can have focus - which is why we're losing the indicator to begin with
// SF doesn't allow cell selection for virtualized grids
export const setLastFocusIndicator = (event: FocusEvent) => {
    document.querySelectorAll('.item-last-focused').forEach((v) => v.classList.remove('item-last-focused'));
    (event.target as HTMLElement).classList.add('item-last-focused');
};

export const switchFocusToTooltipChild = (event: FocusEvent) => {
    setLastFocusIndicator(event);
    const tooltipChild = (event.target as HTMLElement).querySelector('[data-tooltip-host]') as HTMLElement;

    if (tooltipChild) {
        const tooltipContent = tooltipChild.firstElementChild as HTMLElement;

        if (tooltipChild.offsetWidth < tooltipContent.offsetWidth) {
            tooltipContent.focus();
        }
    }
};

export const isMultiColumnReport = (report: IWhitePaperReport | undefined): boolean => {
    if (report?.columns) {
        return report.columns.length > 1;
    }
    return false;
};

const getColumnIdFromFieldName = (fieldName: string): string => {
    if (!fieldName.includes('.')) {
        return fieldName;
    }
    const { entityCode } = getDynamicFieldNameParts(fieldName);
    return entityCode;
};
const isVarianceColumn = (columnFieldName: string): boolean => {
    return columnFieldName?.endsWith(varianceValueFieldNameSuffix) ?? false;
};
const isCurrentValueColumn = (columnFieldName: string): boolean => {
    return columnFieldName?.endsWith(currentValueFieldNameSuffix) ?? false;
};

export const getCellHighlightingClasses: (data: IWhitePaperRow, field: string) => { add: string[]; remove: string[] } = (
    data: IWhitePaperRow,
    field: string
) => {
    const addClasses: string[] = [];
    const removeClasses: string[] = [];
    const cellWasChangedByDrilldown = getCellValueForColumnInWhitePaperRow<boolean>(
        data,
        getColumnIdFromFieldName(field ?? ''),
        (rowCellValue: IWhitePaperComparedValue) => rowCellValue!.isChangedByDrilldown!
    );
    if (cellWasChangedByDrilldown && isCurrentValueColumn(field)) {
        addClasses.push('recalc-needed');
    } else {
        removeClasses.push('recalc-needed');
    }

    const cellWasOverridden = getCellValueForColumnInWhitePaperRow<boolean>(
        data,
        getColumnIdFromFieldName(field ?? ''),
        (rowCellValue: IWhitePaperComparedValue) => rowCellValue!.isOverridden!
    );
    if (cellWasOverridden && isCurrentValueColumn(field)) {
        addClasses.push('highlighted-cell');
    } else {
        removeClasses.push('highlighted-cell');
    }
    const exceedsVariance = getCellValueForColumnInWhitePaperRow<boolean>(
        data,
        getColumnIdFromFieldName(field ?? ''),
        (rowCellValue: IWhitePaperComparedValue) => rowCellValue!.exceedsVariance!
    );
    if (exceedsVariance && isVarianceColumn(field)) {
        addClasses.push('white-paper-report-highlight');
    } else {
        removeClasses.push('white-paper-report-highlight');
    }
    return { add: addClasses, remove: removeClasses };
};

export const getPostAmountOption = (report: Report, translateFunction: TFunction<'translation', undefined, 'translation'>): string => {
    if (report.updateBackReport && report.updateBack) {
        return translateFunction('postAmountAndCreateReport').toString();
    }
    if (report.updateBackReport) {
        return translateFunction('createReportWithoutAmounts').toString();
    }
    return translateFunction('none').toString();
};

export const getCustomGroupName = (customGroups: CustomGroup[] | null | undefined, customGroupKey: number | null | undefined): string => {
    if (customGroupKey === null || customGroupKey === undefined) {
        return '';
    }
    return customGroups?.find((group) => group.customGroupKey === customGroupKey)?.name ?? '';
};

export const getCustomRangeName = (customRanges: CustomRange[] | null | undefined, rangeKey: number | null | undefined): string => {
    if (rangeKey === null || rangeKey === undefined) {
        return '';
    }
    return customRanges?.find((range) => range.rangeKey === rangeKey)?.name ?? '';
};

export interface ReportTableRow extends Report {
    customGroupName: string;
    postAmountGroupName: string;
    customRangeName: string;
    postAmountOption: string;
}

export const getReportTableRows = (
    reports: Report[] | null | undefined,
    customGroups: CustomGroup[] | null | undefined,
    customRanges: CustomRange[] | null | undefined,
    translateFunction: TFunction<'translation', undefined, 'translation'>
): ReportTableRow[] => {
    return (reports ?? []).map((report) => ({
        ...report,
        customGroupName: getCustomGroupName(customGroups, report.columnFileKey),
        postAmountGroupName: getCustomGroupName(customGroups, report.updateColumnFileKey),
        customRangeName: getCustomRangeName(customRanges, report.rangeFileKey),
        postAmountOption: getPostAmountOption(report, translateFunction),
    }));
};

export const adaptReportToShowableTaxReturnItemRecord = (report: Report): ShowableTaxReturnItemRecord => {
    return {
        taxReturnItemId: report.taxReturnItemId,
        taxReturnItemName: report.name || null,
        taxReturnItemKey: report.id,
        taxReturnItemDescription: report.description || null,
        calculationDescription: report.calculationDescription || null,
        calculationName: report.calculationName || null,
        calculationKey: report.calculationKey || undefined,
        taxReturnItemCaseKey: report.caseKey || undefined,
        caseCode: report.case || null,
        entityKey: report.entityKey || undefined,
        entityCode: report.entityCode || null,
        entityLabel: report.entityName || null,
        jurisdictionCode: report.jurisdiction || null,
        taxReturnItemPeriodKey: report.periodKey || undefined,
        taxYearName: report.period || null,
        jurisdictionKey: report.jurisdictionKey || undefined,
        columnFileKey: report.columnFileKey,
        rangeFileKey: report.rangeFileKey,
        includeAllBU: report.includeAllBU || undefined,
        ignoreEntityGroup: report.ignoreEntityGroup || undefined,
        updateColumnFileKey: report.updateColumnFileKey,
        updateBack: report.updateBack || undefined,
        updateBackReport: report.updateBackReport || undefined,
        createdByUserKey: report.createdBy,
        contextKey: report.contextKey ?? 0,
        alwaysRefresh: report.alwaysRefresh ?? true,
    };
};

export const adaptShowableTaxReturnItemRecordToReport = (item: ShowableTaxReturnItemRecord): Report => {
    return {
        taxReturnItemId: item.taxReturnItemId,
        name: item.taxReturnItemName || null,
        id: item.taxReturnItemKey,
        description: item.taxReturnItemDescription || null,
        calculationDescription: item.calculationDescription || null,
        calculationName: item.calculationName || null,
        calculationKey: item.calculationKey,
        caseKey: item.taxReturnItemCaseKey,
        case: item.caseCode || null,
        entityKey: item.entityKey,
        entityCode: item.entityCode || null,
        entityName: item.entityLabel || null,
        jurisdiction: item.jurisdictionCode || null,
        periodKey: item.taxReturnItemPeriodKey,
        period: item.taxYearName || null,
        jurisdictionKey: item.jurisdictionKey || undefined,
        columnFileKey: item.columnFileKey,
        rangeFileKey: item.rangeFileKey,
        includeAllBU: item.includeAllBU,
        ignoreEntityGroup: item.ignoreEntityGroup,
        updateColumnFileKey: item.updateColumnFileKey,
        updateBack: item.updateBack,
        updateBackReport: item.updateBackReport,
    };
};

export const contextChanged = (formTaxReturnData: ShowableTaxReturnItemRecord, originalTaxReturnData?: ShowableTaxReturnItemRecord) => {
    return (
        originalTaxReturnData === undefined ||
        formTaxReturnData.taxReturnItemCaseKey !== originalTaxReturnData.taxReturnItemCaseKey ||
        formTaxReturnData.taxReturnItemPeriodKey !== originalTaxReturnData.taxReturnItemPeriodKey ||
        formTaxReturnData.entityKey !== originalTaxReturnData.entityKey ||
        formTaxReturnData.jurisdictionKey !== originalTaxReturnData.jurisdictionKey
    );
};

export const getIndexFromPDFDataName = (dataName: string): number | undefined => {
    const regex = /\[(\d+)\]$/;
    const match = dataName.match(regex);

    if (match && match[1]) {
        const extractedIndex = parseInt(match[1], 10);
        if (extractedIndex <= 0) {
            return undefined;
        }
        return extractedIndex - 1;
    }
    return undefined;
};

export const getFilteredFormFieldsDrillDown = (
    formFields: ViewFormResponseFormFields | undefined,
    currentWhitePaperReport: IWhitePaperReport | undefined
): Record<string, boolean> => {
    if (!formFields) {
        return {};
    }

    const allowedDrillDownContextObject: Record<string, boolean> = {};

    Object.values(formFields)
        .flat()
        .forEach((item: ReviewFormField) => {
            const existsRow = currentWhitePaperReport?.rows?.some((r) => r.rowNumber === item.row);
            if (item.pdfFieldName && item.isNumeric && item.row && item.row > 0 && existsRow) {
                const index = getIndexFromPDFDataName(item.dataName ?? '');
                const drillDownAllowed =
                    typeof index === 'number' && index >= 0 ? index < (currentWhitePaperReport?.columns?.length ?? 0) : true;
                allowedDrillDownContextObject[item.pdfFieldName as string] = drillDownAllowed;
            }
        });

    return allowedDrillDownContextObject;
};

export interface IResolvedFormFieldReportCell {
    currentColumn: IWhitePaperColumn | undefined;
    row: IWhitePaperRow | undefined;
}

export interface IResolvedFormFieldReportCellWithField extends IResolvedFormFieldReportCell {
    matchField: ReviewFormField;
}

// Resolves a form field back to the report grid cell (column + row) it represents, using the
// same identity a report cell comment is anchored to. Comments added from a PDF field and from
// the report grid for the same underlying cell are the same thread, so this is the mapping
// both the "Enter Amounts" flow (PdfContextualMenu) and form-field comments rely on.
export const resolveReportCellForMatchedField = (
    matchField: ReviewFormField | undefined,
    currentWhitePaperReport: IWhitePaperReport | undefined,
    fallbackColumnIndex?: number
): IResolvedFormFieldReportCell | undefined => {
    if (!matchField || !currentWhitePaperReport) {
        return undefined;
    }

    const columnIndex = getIndexFromPDFDataName(matchField.dataName ?? '');
    // getIndexFromPDFDataName legitimately returns 0 for a dataName ending in [1] (the first
    // column, a common case) - `||` would treat that 0 as missing and fall through to
    // fallbackColumnIndex instead, anchoring the resolved cell to the wrong column.
    const currentColumn = currentWhitePaperReport.columns?.[columnIndex ?? fallbackColumnIndex ?? 0];
    const row = Number.isInteger(matchField.row) ? currentWhitePaperReport.rows?.find((r) => r.rowNumber === matchField.row) : undefined;

    return { currentColumn, row };
};

export const resolveReportCellForFormField = (
    pdfFieldName: string | undefined,
    formFields: ViewFormResponseFormFields | undefined,
    currentWhitePaperReport: IWhitePaperReport | undefined,
    fallbackColumnIndex?: number
): IResolvedFormFieldReportCellWithField | undefined => {
    if (!pdfFieldName || !formFields) {
        return undefined;
    }

    const matchField = Object.values(formFields)
        .flat()
        .find((f) => f.pdfFieldName === pdfFieldName);
    const resolved = resolveReportCellForMatchedField(matchField, currentWhitePaperReport, fallbackColumnIndex);

    return matchField && resolved ? { matchField, ...resolved } : undefined;
};

export const getStatementDataNames = (tocReferenceMap: Map<TaxReturnTOCKey, TaxReturnTOCItem> | undefined): Set<string> => {
    const statementDataNames = new Set<string>();
    if (!tocReferenceMap) {
        return statementDataNames;
    }

    tocReferenceMap.forEach((item) => {
        if (item.isStatement && item.dataName) {
            statementDataNames.add(item.dataName);
        }
    });

    return statementDataNames;
};

export const printFile = (data: Blob) => {
    // Create new blob to load in iframe
    const blob = new Blob([data], { type: 'application/pdf' });
    const blobURL = URL.createObjectURL(blob);

    // Load content in a hidden iframe to call the browser native print window
    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);
    iframe.style.display = 'none';
    iframe.src = blobURL;
    iframe.onload = function () {
        setTimeout(function () {
            iframe.focus();
            iframe.contentWindow?.print();
        }, 1);
    };
};
